import { Temporal } from '@js-temporal/polyfill';
import { CoordinatorService } from './coordinator.service';
import { TIER, tierLs } from './license$';

export interface LicenseFeatures {
    maxGraphs: number;
    maxNodes: number;
    storageLimit: string;
    apiCallsPerDay: number;
    premiumSupport: boolean;
    exportFormats: string[];
    collaborators: number;
}

export interface LicenseLimitations {
    watermark: boolean;
    analytics: boolean;
    customBranding: boolean;
}

export interface LicenseData {
    tier: TIER;
    name: string;
    valid: boolean;
    status: 'active' | 'expired' | 'revoked' | 'pending';
    userId: string;
    licenseId: string;
    issuedAt: string;
    validUntil: string;
    features: LicenseFeatures;
    limitations: LicenseLimitations;
}

export interface LemonSubscriptionsData {
    userId: string;
    totalCount: number;
    activeCount: number;
    errors: string[];
    subscriptions: any[];
}

export interface InstitutionalLicensesData {
    userId: string;
    totalCount: number;
    domainMatches: number;
    directMemberships: number;
    licenses: any[];
}

export interface LicenseGetResponse {
    license: LicenseData;
    cached: boolean;
    forceRefresh: boolean;
    lemonSubscriptions?: LemonSubscriptionsData;
    institutionalLicenses?: InstitutionalLicensesData;
    licenseSource?: {
        finalSource: 'base' | 'lemon' | 'institutional';
        sourceDetails?: any;
        upgradeApplied?: boolean;
        originalTier?: string;
    };
}

export class License {
    tier: TIER;
    name: string;
    valid: boolean;
    status: 'active' | 'expired' | 'revoked' | 'pending';
    userId: string;
    licenseId: string;
    issuedAt: string;
    validUntil: string;
    features: LicenseFeatures;
    limitations: LicenseLimitations;
    
    // Additional data from full response
    lemonSubscriptions?: LemonSubscriptionsData;
    institutionalLicenses?: InstitutionalLicensesData;
    licenseSource?: {
        finalSource: 'base' | 'lemon' | 'institutional';
        sourceDetails?: any;
        upgradeApplied?: boolean;
        originalTier?: string;
    };

    constructor(
        private coords: CoordinatorService,
        response: LicenseGetResponse | LicenseData,
    ) {
        // Check if this is a full response or just license data
        const licenseData = 'license' in response ? response.license : response;
        
        this.tier = licenseData.tier ?? 'free';
        this.name = licenseData.name ?? 'Free License';
        this.valid = licenseData.valid ?? true;
        this.status = licenseData.status ?? 'active';
        this.userId = licenseData.userId ?? '';
        this.licenseId = licenseData.licenseId ?? '';
        this.issuedAt = licenseData.issuedAt ?? new Date().toISOString();
        this.validUntil = licenseData.validUntil ?? '2099-12-31T23:59:59.999Z';
        
        // Set features with defaults
        this.features = licenseData.features ?? {
            maxGraphs: 3,
            maxNodes: 100,
            storageLimit: '100MB',
            apiCallsPerDay: 1000,
            premiumSupport: false,
            exportFormats: ['json'],
            collaborators: 0,
        };
        
        // Set limitations with defaults
        this.limitations = licenseData.limitations ?? {
            watermark: true,
            analytics: false,
            customBranding: false,
        };
        
        // Store additional data if full response
        if ('license' in response) {
            this.lemonSubscriptions = response.lemonSubscriptions;
            this.institutionalLicenses = response.institutionalLicenses;
            this.licenseSource = response.licenseSource;
        }
    }

    get title() {
        return this.name;
    }

    get description() {
        // Generate description based on tier and features
        if (this.tier === 'free') {
            return this.coords.text("Free_license_desc");
        }
        return this.coords.text(`${this.tier}_license_desc`);
    }

    get indicatorText() {
        return this.coords.text(this.tier);
    }

    get indicatorClass() {
        return this.tier;
    }

    get isExpired() {
        return this.status === 'expired' || !this.valid;
    }

    get hasLemonSubscription() {
        return this.lemonSubscriptions && this.lemonSubscriptions.activeCount > 0;
    }

    get hasInstitutionalLicense() {
        return this.institutionalLicenses && this.institutionalLicenses.totalCount > 0;
    }

    // Check if user can access a specific feature
    canAccessFeature(featureName: string): boolean {
        switch (featureName) {
            case 'premium-support':
                return this.features.premiumSupport;
            case 'custom-branding':
                return this.limitations.customBranding;
            case 'analytics':
                return this.limitations.analytics;
            case 'pdf-export':
                return this.features.exportFormats.includes('pdf');
            case 'svg-export':
                return this.features.exportFormats.includes('svg');
            default:
                return false;
        }
    }

    // Operator to compare licenses based on tier priority
    static compare(
        licenseA: License | null | undefined,
        licenseB: License | null | undefined,
    ): boolean {
        const tierOrder = tierLs;
        const tierAIndex = licenseA ? tierOrder.indexOf(licenseA.tier) : -1;
        const tierBIndex = licenseB ? tierOrder.indexOf(licenseB.tier) : -1;
        return tierAIndex >= tierBIndex;
    }
}
